import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal } from '@todo/core/open-external';

import type { PartnerLink } from '../lib/collections';

import buttonCss from './book-link.module.css';
import css from './partner-links.module.css';

// A collection page's "more on a partner's site" band, under its tours: a
// line of copy and one button per partner search (Collection.partnerLinks —
// miami: Sailo's boats). Real <a href>s with rel="sponsored nofollow", each
// click through openExternal (the `affiliate-click` event, id = the page's
// slug). No partner script, banner or widget. Copy:
// `collections.<slug>.partnerTitle` / `.partnerText` where the page has its
// own, else `partners.title` / `.text`; `partners.button` ({{name}} optional).
export function PartnerLinks({ slug, links }: { slug: string; links: readonly PartnerLink[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  if (!links.length) return null;
  const title = t(`collections.${slug}.partnerTitle`, { defaultValue: t('partners.title') });
  const text = t(`collections.${slug}.partnerText`, { defaultValue: t('partners.text') });
  const pageTitle = t(`collections.${slug}.title`);

  return (
    <aside className={css.band} aria-labelledby={`partners-${slug}`}>
      <div className={css.copy}>
        <h2 id={`partners-${slug}`} className={css.title}>
          {title}
        </h2>
        <p className={css.text}>{text}</p>
      </div>
      <div className={css.actions}>
        {links.map((link) => {
          const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            openExternal(link.url, { kind: link.kind, source: link.source, id: slug, title: pageTitle, locale });
          };
          return (
            <a
              key={link.source}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer sponsored nofollow"
              onClick={onClick}
              className={buttonCss.button}>
              {t('partners.button', { name: link.name })}
            </a>
          );
        })}
      </div>
    </aside>
  );
}
